import { FC, useCallback } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { PAYMENT_BEHAVIOUR } from '@savgroup-front-common/constants';
import { LinkButton } from '@savgroup-front-common/core/atoms/link';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { useRoutedStepsOrchestratorContext } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.context';
import ArrowNakedRightIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedUp.icon';
import { OrderDetailsProductWithOpenedClaim } from '@savgroup-front-common/types';
import { PaymentProvider } from '@savgroup-front-common/types/Payment/PaymentProvider';

import { FINAL_ROUTES } from '../../../../../../app';
import { useGetPaymentAccount } from '../../../../../../components/Payments/hooks/useGetPaymentAccount';
import { useGetClaimGroupConfirmation } from '../../../../hooks/useGetClaimGroupConfirmation';
import { ClaimGroupReasonPageStepValues } from '../../../../NewClaimGroupReasonPage/NewClaimGroupReasonPage.types';
import { $OpenFile } from '../ChooseProductCard.styles';
import messages from '../messages';

interface FinalizeClaimGroupProps {
  product: OrderDetailsProductWithOpenedClaim;
}

export const FinalizeClaimGroup: FC<FinalizeClaimGroupProps> = ({
  product,
}) => {
  const { onValuesUpdate } =
    useRoutedStepsOrchestratorContext<ClaimGroupReasonPageStepValues>();
  const navigate = useNavigate();

  const { claimGroupConfirmation } = useGetClaimGroupConfirmation({
    claimGroupId: product?.claimGroupId,
    suspense: true,
  });
  const { paymentAccountDto } = useGetPaymentAccount({
    claimGroupId: product?.claimGroupId,
    shouldToastError: false,
  });

  const handleFinalizeClick = useCallback(() => {
    onValuesUpdate({
      newValues: {
        claims: undefined,
        claimGroupId: product.claimGroupId,
        diagnosticTree: {
          diagnosticTreeUniqueName: undefined,
          isDiagnosticIsAlreadyMade: false,
          hasRefusedDiagnosticTree: false,
        },
      },
    });

    if (
      claimGroupConfirmation?.paymentBehavior ===
        PAYMENT_BEHAVIOUR.PAYMENT_REQUIRED &&
      paymentAccountDto?.paymentProvider === PaymentProvider.MONEXT
    ) {
      navigate(
        generatePath(FINAL_ROUTES.CLAIM_GROUP_CONFIRMATION, {
          claimGroupId: product.claimGroupId,
        }),
      );
    } else {
      navigate(
        generatePath(FINAL_ROUTES.CLAIM_GROUP_REASON, {
          claimGroupId: product.claimGroupId,
        }),
      );
    }
  }, [
    claimGroupConfirmation?.paymentBehavior,
    navigate,
    onValuesUpdate,
    paymentAccountDto?.paymentProvider,
    product.claimGroupId,
  ]);

  if (!product.claimGroupId) {
    return null;
  }

  return (
    <$OpenFile>
      <LinkButton onClick={handleFinalizeClick}>
        <SafeFormattedMessageWithoutSpread
          message={messages.followClaimGroupLink}
        />
      </LinkButton>
      <ArrowNakedRightIcon size="14px" />
    </$OpenFile>
  );
};
